import {
  STANDING_ADULT,
  classifyInstallationTypology,
  inferOpeningKind,
  inferRoomFunction,
  inferSurfaceActivity,
  isSupportingTypology,
  isSuspendedInstallation,
  isSuspendedTypology,
  summarizeEnvironmentReading,
  type EnvironmentElement,
  type EnvironmentLine,
  type EnvironmentReading,
  type EnvironmentSurface,
  type ObservedInstallation,
  type RoomFunction,
} from "@calcplan/ai-capabilities";
import type { Room, SceneDocument, SceneElement, Vec2, Wall } from "@calcplan/domain";
import { elementComponentry } from "./ai-element-componentry";
import { checkElementInstallation } from "./ai-scene-checks";
import { readElementPlacementSurface } from "./placement";
import {
  renderedElementCenterMm,
  renderedElementSizeMm,
  renderedElementVerticalIntervalMm,
} from "./rendered-envelope";

/**
 * Leitura estruturada do ambiente, produzida pelo sistema a partir da cena:
 * função do cômodo, perfil de alcance, pé-direito, linhas existentes por
 * parede, elementos com faixa vertical e tipologia observada, superfícies de
 * uso com atividade provável. É a mesma leitura que o planejador recebe antes
 * de decidir, que o validador do plano usa para conferir cota e linha, e que
 * o crítico recebe como contexto — sem nenhum valor escolhido pelo executor.
 */

export interface EnvironmentReadingRequest {
  roomId?: string | null;
  wallIds?: ReadonlyArray<string> | null;
  focusElementIds?: ReadonlyArray<string> | null;
}

export interface RoomProfile {
  function?: RoomFunction | null;
  users?: string | null;
  notes?: string | null;
}

export const ROOM_PROFILE_EXTENSION_KEY = "calcplanRoomProfile";

export function readRoomProfile(room: Room | null | undefined): RoomProfile | null {
  const raw = room?.extensions?.[ROOM_PROFILE_EXTENSION_KEY];
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  return {
    function: typeof record.function === "string" ? record.function as RoomFunction : null,
    users: typeof record.users === "string" ? record.users : null,
    notes: typeof record.notes === "string" ? record.notes : null,
  };
}

const LINE_MERGE_TOLERANCE_MM = 5;
const WALL_BACKING_GAP_MM = 60;

function pointInsidePolygon(point: Vec2, polygon: Vec2[]): boolean {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const currentPoint = polygon[index]!;
    const previousPoint = polygon[previous]!;
    const intersects = ((currentPoint[1] > point[1]) !== (previousPoint[1] > point[1]))
      && point[0] < (previousPoint[0] - currentPoint[0]) * (point[1] - currentPoint[1])
        / ((previousPoint[1] - currentPoint[1]) || Number.EPSILON) + currentPoint[0];
    if (intersects) inside = !inside;
  }
  return inside;
}

function roomContains(room: Room, centerMm: [number, number, number]): boolean {
  const point: Vec2 = [centerMm[0], centerMm[2]];
  return pointInsidePolygon(point, room.boundary) && !room.holes.some((hole) => pointInsidePolygon(point, hole));
}

function selectRoom(scene: SceneDocument, request: EnvironmentReadingRequest, elements: SceneElement[]): Room | null {
  if (request.roomId) return scene.rooms.find((room) => room.id === request.roomId) ?? null;
  if (!scene.rooms.length) return null;
  const focus = (request.focusElementIds ?? [])
    .map((id) => elements.find((element) => element.id === id))
    .filter((element): element is SceneElement => Boolean(element));
  for (const element of focus) {
    const room = scene.rooms.find((candidate) => roomContains(candidate, renderedElementCenterMm(element)));
    if (room) return room;
  }
  if (scene.rooms.length === 1) return scene.rooms[0]!;
  let best: { room: Room; count: number } | null = null;
  for (const room of scene.rooms) {
    const count = elements.filter((element) => roomContains(room, renderedElementCenterMm(element))).length;
    if (!best || count > best.count) best = { room, count };
  }
  return best?.room ?? null;
}

function wallLengthMm(wall: Wall): number {
  return Math.hypot(wall.endMm[0] - wall.startMm[0], wall.endMm[1] - wall.startMm[1]);
}

function elementWallId(scene: SceneDocument, element: SceneElement, installation: ReturnType<typeof checkElementInstallation>): string | null {
  const surface = readElementPlacementSurface(scene, element);
  if (surface?.kind === "wall" && surface.wallId) return surface.wallId;
  const allowedGap = Math.max(WALL_BACKING_GAP_MM, renderedElementSizeMm(element)[2] * 0.25);
  const nearest = installation.nearestWalls.find((wall) => wall.gapMm <= allowedGap);
  return nearest?.wallId ?? null;
}

function mergeLines(lines: EnvironmentLine[]): EnvironmentLine[] {
  const merged: EnvironmentLine[] = [];
  for (const line of [...lines].sort((left, right) => left.heightMm - right.heightMm)) {
    const existing = merged.find((candidate) => (
      candidate.kind === line.kind
      && candidate.family === line.family
      && Math.abs(candidate.heightMm - line.heightMm) <= LINE_MERGE_TOLERANCE_MM
    ));
    if (!existing) {
      merged.push({ ...line, ...(line.sourceElementId ? { sourceElementIds: [line.sourceElementId] } : {}) });
      continue;
    }
    const names = [existing.sourceName, line.sourceName].filter((name): name is string => Boolean(name));
    const unique = [...new Set(names.flatMap((name) => name.split(", ")))];
    existing.sourceName = unique.slice(0, 3).join(", ") + (unique.length > 3 ? ` (+${unique.length - 3})` : "");
    if (!existing.sourceElementId) existing.sourceElementId = line.sourceElementId;
    if (line.sourceElementId) existing.sourceElementIds = [...new Set([...(existing.sourceElementIds ?? []), line.sourceElementId])];
  }
  return merged;
}

export function buildEnvironmentReading(scene: SceneDocument, request: EnvironmentReadingRequest = {}): EnvironmentReading {
  const visible = scene.elements.filter((element) => element.visible !== false);
  const room = selectRoom(scene, request, visible);
  const profile = readRoomProfile(room);
  const roomElements = room ? visible.filter((element) => roomContains(room, renderedElementCenterMm(element))) : visible;
  const level = (room ? scene.levels.find((candidate) => candidate.id === room.levelId) : null) ?? scene.levels[0] ?? null;
  const floorElevationMm = level?.elevationMm ?? 0;
  const ceilingHeightMm = level?.heightMm ?? (scene.walls[0]?.heightMm ?? 2_700);

  const elements: EnvironmentElement[] = [];
  const notes: string[] = [];
  const installationsById = new Map<string, ReturnType<typeof checkElementInstallation>>();
  for (const element of roomElements) {
    const installation = checkElementInstallation(scene, element);
    installationsById.set(element.id, installation);
    const [bottomAbs, topAbs] = renderedElementVerticalIntervalMm(element);
    const bottom = bottomAbs - floorElevationMm;
    const top = topAbs - floorElevationMm;
    const size = renderedElementSizeMm(element);
    const center = renderedElementCenterMm(element);
    const yaw = element.transform.rotationRad[1] ?? 0;
    const extentX = (Math.abs(size[0] * Math.cos(yaw)) + Math.abs(size[2] * Math.sin(yaw))) / 2;
    const extentZ = (Math.abs(size[0] * Math.sin(yaw)) + Math.abs(size[2] * Math.cos(yaw))) / 2;
    const wallId = elementWallId(scene, element, installation);
    const observed: ObservedInstallation = {
      support: installation.support.expected === "unconstrained" ? "unconstrained" : installation.support.expected,
      bottomHeightMm: bottom,
      topHeightMm: top,
      floating: installation.floor.gapMm > 2,
      wallBacked: wallId !== null,
    };
    const typology = classifyInstallationTypology({
      name: element.name,
      category: element.catalogRef?.itemId ?? null,
      dimensionsMm: element.dimensionsMm,
      installation: observed,
    });
    const componentry = elementComponentry(element);
    const suspended = isSuspendedTypology(typology.kind) ? bottom > 50 : isSuspendedInstallation(observed);
    if (typology.basis === "installation" || typology.basis === "proportion") {
      notes.push(`${element.name}: tipologia ${typology.label} inferida por ${typology.basis === "installation" ? "estado de instalação" : "proporção"} (nome não identifica).`);
    }
    elements.push({
      elementId: element.id,
      name: element.name,
      wallId,
      typology: { kind: typology.kind, label: typology.label, confidence: typology.confidence, basis: typology.basis ?? "none" },
      mount: observed.support,
      suspended,
      bottomMm: Math.round(bottom),
      topMm: Math.round(top),
      widthMm: Math.round(size[0]),
      depthMm: Math.round(size[2]),
      openingKind: inferOpeningKind({ name: element.name }),
      footprintMm: {
        minXMm: Math.round(center[0] - extentX),
        maxXMm: Math.round(center[0] + extentX),
        minZMm: Math.round(center[2] - extentZ),
        maxZMm: Math.round(center[2] + extentZ),
      },
      ...(componentry ? { componentry } : {}),
    });
  }

  const roomFunction = inferRoomFunction({
    roomName: room?.name ?? null,
    elements: elements.map((element) => ({ name: element.name, category: null, kind: element.typology.kind })),
    declared: profile?.function ?? null,
  });
  if (roomFunction.confidence === "undefined") {
    notes.push(`Função do ambiente indefinida: ${roomFunction.evidence.join("; ") || "sem evidência"}. Declare a premissa no plano.`);
  }

  const surfaces: EnvironmentSurface[] = elements
    .filter((element) => !element.suspended && isSupportingTypology(element.typology.kind) && element.typology.kind !== "tall_unit")
    .map((element) => {
      const activity = inferSurfaceActivity({
        lowerTypology: element.typology.kind,
        roomFunction: roomFunction.value,
        surfaceTopMm: element.topMm,
        typologyConfidence: element.typology.confidence,
      });
      return {
        elementId: element.elementId,
        name: element.name,
        topMm: element.topMm,
        depthMm: element.depthMm,
        activity: activity.activity,
        confidence: activity.confidence,
        basis: activity.basis,
      };
    });

  const wallFilter = request.wallIds?.length ? new Set(request.wallIds) : null;
  const walls = scene.walls
    .filter((wall) => !wallFilter || wallFilter.has(wall.id))
    .filter((wall) => !room || wall.levelId === room.levelId)
    .map((wall) => {
      const onWall = elements.filter((element) => element.wallId === wall.id);
      const lines: EnvironmentLine[] = [];
      for (const element of onWall) {
        // Linhas de composição vêm de mobiliário fixado na parede ou apoiado
        // no piso. Ponto de instalação (tomada, interruptor) é interferência
        // (§18.9), nunca linha; item apoiado em outro móvel (almofada no sofá,
        // caixa sobre o tampo) herda a composição da base — listar esses como
        // "linha de elementos suspensos" alimentava objeções de alinhamento
        // contra uma tomada a 171 mm (20/08 13:38).
        if (element.typology.kind === "fixture" || element.mount === "element") continue;
        const family = element.suspended ? "suspended" as const : "floor-standing" as const;
        lines.push({ heightMm: element.topMm, kind: "top", family, sourceElementId: element.elementId, sourceName: element.name });
        if (element.suspended) {
          lines.push({ heightMm: element.bottomMm, kind: "bottom", family, sourceElementId: element.elementId, sourceName: element.name });
        }
      }
      for (const opening of scene.openings.filter((candidate) => candidate.wallId === wall.id)) {
        const label = opening.kind === "door" ? "porta" : "janela";
        lines.push({ heightMm: opening.sillMm + opening.heightMm, kind: "opening-head", family: "opening", sourceElementId: opening.id, sourceName: label });
        if (opening.sillMm > 50) lines.push({ heightMm: opening.sillMm, kind: "opening-sill", family: "opening", sourceElementId: opening.id, sourceName: label });
      }
      lines.push({ heightMm: Math.min(ceilingHeightMm, wall.heightMm || ceilingHeightMm), kind: "ceiling", family: "envelope" });
      return {
        wallId: wall.id,
        lengthMm: Math.round(wallLengthMm(wall)),
        lines: mergeLines(lines).map((line) => ({ ...line, heightMm: Math.round(line.heightMm) })),
        elementIds: onWall.map((element) => element.elementId),
      };
    });

  const reading: EnvironmentReading = {
    roomId: room?.id ?? null,
    roomName: room?.name ?? null,
    roomFunction,
    userProfile: {
      id: STANDING_ADULT.id,
      label: profile?.users ? `${STANDING_ADULT.label}; perfil declarado: ${profile.users}` : STANDING_ADULT.label,
      statureMm: STANDING_ADULT.statureMm,
      reach: {
        primeMm: STANDING_ADULT.reach.primeMm,
        acceptableMm: STANDING_ADULT.reach.acceptableMm,
        headZoneMm: STANDING_ADULT.reach.headZoneMm,
        storageTopMaxMm: STANDING_ADULT.reach.storageTopMaxMm,
        clearUnderFixedInCirculationMm: STANDING_ADULT.reach.clearUnderFixedInCirculationMm,
      },
    },
    ceilingHeightMm: Math.round(ceilingHeightMm),
    walls,
    elements,
    surfaces,
    notes,
    summary: "",
  };
  reading.summary = summarizeEnvironmentReading(reading);
  return reading;
}

/** Leitura de um único elemento como descritor de tipologia (nome, catálogo, dimensões, estado observado). */
export function observedTypologyDescriptor(scene: SceneDocument, element: SceneElement): {
  name: string;
  category: string | null;
  dimensionsMm: SceneElement["dimensionsMm"];
  installation: ObservedInstallation;
} {
  const installation = checkElementInstallation(scene, element);
  const level = scene.levels[0];
  const floorElevationMm = level?.elevationMm ?? 0;
  const [bottomAbs, topAbs] = renderedElementVerticalIntervalMm(element);
  return {
    name: element.name,
    category: element.catalogRef?.itemId ?? null,
    dimensionsMm: element.dimensionsMm,
    installation: {
      support: installation.support.expected === "unconstrained" ? "unconstrained" : installation.support.expected,
      bottomHeightMm: bottomAbs - floorElevationMm,
      topHeightMm: topAbs - floorElevationMm,
      floating: installation.floor.gapMm > 2,
      wallBacked: elementWallId(scene, element, installation) !== null,
    },
  };
}
